import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Globe,
  ArrowLeft,
  MapPin,
  Plane,
  Home,
  FileText,
  Building2,
  CreditCard,
  Heart,
  Bus,
  Users,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountryGuides = ({ user }) => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(user?.target_country || "ireland");
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuide();
  }, [selectedCountry]);

  const fetchGuide = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/guides/${selectedCountry}`, {
        credentials: "include",
      });
      if (response.ok) {
        setGuide(await response.json());
      }
    } catch (error) {
      console.error("Error fetching guide:", error);
    } finally {
      setLoading(false);
    }
  };

  const countryImages = {
    ireland: "https://images.unsplash.com/photo-1565882135091-8e52d4c81762?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxpcmVsYW5kJTIwZ3JlZW4lMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzcxMTE0MTkyfDA&ixlib=rb-4.1.0&q=85",
    uk: "https://images.unsplash.com/photo-1764079648275-a37d1f5104ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBjaXR5JTIwc3RyZWV0JTIwY296eXxlbnwwfHx8fDE3NzExMTQxOTh8MA&ixlib=rb-4.1.0&q=85",
    canada: "https://images.unsplash.com/photo-1542190914-0771f5603026?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYW5hZGElMjBtb3VudGFpbnMlMjBsYWtlfGVufDB8fHx8MTc3MTExNDIwMHww&ixlib=rb-4.1.0&q=85",
  };

  const guideIcons = {
    visa: FileText,
    documents: FileText,
    housing: Building2,
    banking: CreditCard,
    first_week: Plane,
    healthcare: Heart,
    transport: Bus,
    culture: Users,
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Country Guides</span>
            </div>
          </div>
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger data-testid="country-select" className="w-48 bg-white border-[#E2E8F0]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ireland">🇮🇪 Ireland</SelectItem>
              <SelectItem value="uk">🇬🇧 United Kingdom</SelectItem>
              <SelectItem value="canada">🇨🇦 Canada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : guide ? (
          <>
            {/* Hero */}
            <div className="relative rounded-2xl overflow-hidden mb-8 h-[250px]">
              <img
                src={countryImages[selectedCountry]}
                alt={guide.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-4">
                  <span className="text-5xl">{guide.flag}</span>
                  <div>
                    <h1 className="font-heading text-3xl font-bold text-white">{guide.name}</h1>
                    <div className="flex gap-4 text-white/80 text-sm mt-2">
                      <span>Currency: {guide.currency}</span>
                      <span>Language: {guide.language}</span>
                      <span>Timezone: {guide.timezone}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="preparing" className="space-y-6">
              <TabsList className="bg-[#F5F5F0] p-1 rounded-full">
                <TabsTrigger
                  data-testid="tab-preparing"
                  value="preparing"
                  className="rounded-full px-6 data-[state=active]:bg-[#4A6C6F] data-[state=active]:text-white"
                >
                  <Plane className="w-4 h-4 mr-2" />
                  Preparing to Move
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-landed"
                  value="landed"
                  className="rounded-full px-6 data-[state=active]:bg-[#4A6C6F] data-[state=active]:text-white"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Already Landed
                </TabsTrigger>
              </TabsList>

              <TabsContent value="preparing" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(guide.preparing).map(([key, value]) => {
                    const Icon = guideIcons[key] || Globe;
                    return (
                      <Card
                        key={key}
                        data-testid={`guide-preparing-${key}`}
                        className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl"
                      >
                        <CardHeader>
                          <CardTitle className="flex items-center gap-3 text-[#1A202C] capitalize">
                            <div className="w-10 h-10 bg-[#4A6C6F]/10 rounded-xl flex items-center justify-center">
                              <Icon className="w-5 h-5 text-[#4A6C6F]" />
                            </div>
                            {key.replace(/_/g, " ")}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-[#4A5568] leading-relaxed">{value}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="landed" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(guide.landed).map(([key, value]) => {
                    const Icon = guideIcons[key] || Globe;
                    return (
                      <Card
                        key={key}
                        data-testid={`guide-landed-${key}`}
                        className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl"
                      >
                        <CardHeader>
                          <CardTitle className="flex items-center gap-3 text-[#1A202C] capitalize">
                            <div className="w-10 h-10 bg-[#D97757]/10 rounded-xl flex items-center justify-center">
                              <Icon className="w-5 h-5 text-[#D97757]" />
                            </div>
                            {key.replace(/_/g, " ")}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-[#4A5568] leading-relaxed">{value}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-12 text-[#718096]">
            <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No guide available for this country</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default CountryGuides;
